import { CampaignEditorState } from '../../../contexts/CampaignEditorState';
import {
  AllowList,
  OnsiteCampaignResponse,
  OnsiteCampaignStatus,
  OnsiteMessageSegment,
  SendEmailMessage,
  SubjectLineResponse,
  TemplateResponse,
  TriggerType,
  WebsiteContentMessage,
} from 'sections/onsite-campaigns/types';
import { Branches } from '../../../tabs/CampaignWorkflowTab/state/Branches/BranchesState';
import { DrawerType } from '../../../tabs/CampaignWorkflowTab/state/Drawer/DrawerState';
import { drawerInitialState } from '../../../tabs/CampaignWorkflowTab/state/Drawer/drawerInitialState';
import { TriggerState } from '../../../tabs/CampaignWorkflowTab/components/Workflows/Trigger/state/TriggerState';
import { convertSegmentRuleValues } from '../utils/convertSegmentRuleValues';
import { MessagesMapper } from './MessagesMapper';
import { BranchesMapper } from './BranchesMapper';
import { SplitTestsState } from '../../../tabs/CampaignWorkflowTab/state/SplitTests/SplitTestsState';
import { SplitTestsMapper } from './SplitTestsMapper';
import { convertToMinutes } from '@salecycle/ui-component-library';
import { CampaignScheduling } from '../../../tabs/CampaignWorkflowTab/state/CampaignScheduling/CampaignSchedulingState';
import { formatTime } from '../utils/datetime';

export type CampaignResponseModel = Omit<OnsiteCampaignResponse, 'onsiteMessageSegments'> & {
  onsiteMessageSegments: (OnsiteMessageSegment & { branchId: string })[];
};

export class CampaignResponseToStateAdapter implements CampaignEditorState {
  public constructor(
    campaignResponse: CampaignResponseModel,
    templateResponse: TemplateResponse,
    subjectLineResponse: SubjectLineResponse,
    preferredLanguage: string,
  ) {
    // Settings
    this.name = campaignResponse.name;
    this.status = campaignResponse.status;
    this.allowList = campaignResponse.allowList;
    this.emailAllowList = campaignResponse.emailAllowList;
    this.lookBackPeriodInMinutes = campaignResponse.lookBackPeriodInMinutes ?? convertToMinutes(24, 'hours');

    const startDate = campaignResponse.startDate ? new Date(campaignResponse.startDate) : undefined;
    const endDate = campaignResponse.endDate ? new Date(campaignResponse.endDate) : undefined;
    this.scheduling = {
      startDate,
      startTime: startDate ? `${formatTime(startDate.getHours())}:${formatTime(startDate.getMinutes())}` : '00:00',
      endDate,
      endTime: endDate ? `${formatTime(endDate.getHours())}:${formatTime(endDate.getMinutes())}` : '23:59',
    };

    // Trigger
    const selectedTriggers = [];
    if (campaignResponse.triggers?.exitFrame) selectedTriggers.push(TriggerType.ExitIntent);
    if (campaignResponse.triggers?.onLoad) selectedTriggers.push(TriggerType.PageLoad);
    if (campaignResponse.triggers?.inactivity) selectedTriggers.push(TriggerType.Inactivity);
    if (campaignResponse.triggers?.onClick) selectedTriggers.push(TriggerType.OnClick);

    this.trigger = {
      exitIntentAreas: campaignResponse.triggers?.exitFrame ?? [],
      inactivity: campaignResponse.triggers?.inactivity ? campaignResponse.triggers.inactivity / 1000 : undefined,
      onPageLoad: campaignResponse.triggers?.onLoad,
      onClick: campaignResponse.triggers?.onClick,
      selectedTriggers,
      segmentation: convertSegmentRuleValues(campaignResponse.triggerSegmentationRules),
      frequency: campaignResponse.frequency,
    };

    // Messages
    this.messages = MessagesMapper.Map(
      campaignResponse as CampaignResponseModel,
      templateResponse,
      subjectLineResponse,
      preferredLanguage,
    );

    // Branches
    const branches = BranchesMapper.Map(campaignResponse);
    this.areBranchesLoadedFromServer = !!Object.keys(branches).length;
    this.branches = branches;

    // Drawer
    this.drawer = drawerInitialState.drawer;

    // Split Tests
    this.splitTests = SplitTestsMapper.Map(campaignResponse as CampaignResponseModel);
  }

  public edit?: Partial<Omit<CampaignEditorState, 'edit'>>;
  public drawer: { type: DrawerType; id: string; open: boolean };

  public trigger: TriggerState;
  public messages: {
    WebsiteContent?: WebsiteContentMessage;
    SendEmail?: SendEmailMessage;
  };
  public areBranchesLoadedFromServer: boolean;
  public branches: Branches;
  public name: string;
  public status: OnsiteCampaignStatus;
  public allowList: AllowList[];
  public emailAllowList: string[];
  public lookBackPeriodInMinutes: number;
  public splitTests: SplitTestsState;
  public scheduling: CampaignScheduling;
}
